import React from "react";
import styled from "styled-components";
import { Link } from "gatsby";
import { Container, Row, Col } from "react-bootstrap";
import { useAuth, hasRole, hasTag } from "../../context/AuthContext";

const SectionStyled = styled.section`
  padding: 96px 0 64px;
  background: var(--nd-black);
`;

const SectionLabel = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 36px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.02em;
  line-height: 1.1;
  margin-bottom: 8px;
`;

const SectionDesc = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  max-width: 520px;
  margin-bottom: 48px;
`;

const ToolCard = styled.div`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  transition: border-color 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    border-color: var(--nd-border-visible);
  }
`;

const ToolCardLink = styled(Link)`
  display: block;
  height: 100%;
  text-decoration: none !important;
  color: inherit !important;

  &:hover > div {
    border-color: var(--nd-text-display) !important;
  }
`;

const ToolLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin-bottom: 16px;
  display: block;
`;

const ToolName = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 24px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.01em;
  line-height: 1.2;
  margin-bottom: 8px;
`;

const ToolDesc = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  margin-bottom: 24px;
  flex-grow: 1;
`;

const ToolMeta = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const MetaTag = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  border: 1px solid var(--nd-border-visible);
  border-radius: 4px;
  padding: 4px 12px;
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ color }) => color || "var(--nd-success)"};
  margin-right: 8px;
  vertical-align: middle;
`;

const StatusLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

// Each tool declares minRole. Public-facing tools (PRACTICE / GROWTH /
// MENTORSHIP / KNOWLEDGE / VISIBILITY) stay visible to everyone. Role-
// gated tools (Time Tracker, Staffing admin views, Admin/Sales pages)
// only render for users with the matching role.
//
// Roles ladder (from AuthContext): public(0) → client(1) → staff(2) → admin(3)
// hasRole(current, required) returns true if current >= required.
const tools = [
  // ── Public (everyone sees these) ─────────────────────────────────
  {
    label: "PRACTICE",
    name: "DevOps Labs",
    description:
      "40 challenges that teach DevOps the way we learned — by building something, watching it fail, and figuring out why. Because reading docs isn't practice.",
    tags: ["HANDS-ON", "8 CATEGORIES", "40 CHALLENGES"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/labs",
    minRole: "public",
  },
  {
    label: "GROWTH",
    name: "Career Path",
    description:
      "Interactive roadmap from junior to staff. Track your skills, find resources, see what's next. Built from our actual mentorship program.",
    tags: ["5 LEVELS", "30 SKILLS", "SELF-ASSESSMENT"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/career-path",
    minRole: "public",
  },
  {
    label: "MENTORSHIP",
    name: "Junior Staffing",
    description:
      "We place junior engineers in your team and mentor them. They learn your stack and become your people. If they're great, hire them. That's the point.",
    tags: ["EMBEDDED", "MENTORED", "HUMAN-FIRST"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/staffing",
    minRole: "public",
  },
  {
    label: "KNOWLEDGE",
    name: "Runbook Builder",
    description:
      "Incident response docs and operational runbooks. We write them as we work so you're never stuck if we leave.",
    tags: ["DOCUMENTATION", "HANDOVER", "TEAM-OWNED"],
    status: "COMING SOON",
    statusColor: "var(--nd-warning)",
    href: null,
    minRole: "public",
  },
  {
    label: "VISIBILITY",
    name: "Infrastructure Dashboard",
    description:
      "One screen: every junior on every project, with the tech stacks in play. Project names obfuscated.",
    tags: ["LIVE-MAP", "DELIVERY", "SHARED-WITH-CLIENTS"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/infrastructure",
    minRole: "client",
  },
  // ── Staff (juniors + senior engineers — everyone with role >= staff) ─
  {
    label: "OPERATIONS",
    name: "Time Tracker",
    description:
      "Log hours per project, submit for admin approval, download CSV. The CLI ships with it for terminal-first logging.",
    tags: ["REPORTING", "PER-CLIENT", "CLI-FRIENDLY"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/time-tracker",
    minRole: "staff",
  },
  // ── Admin only ───────────────────────────────────────────────────
  {
    label: "ADMIN · USERS",
    name: "User Management",
    description:
      "Promote, demote, audit users across the platform. Self-edit disabled. Every change writes to the audit log automatically.",
    tags: ["ROLE FLIPS", "EMAIL LOOKUP", "ADMIN-ONLY"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/admin",
    minRole: "admin",
  },
  {
    label: "ADMIN · COMPLIANCE",
    name: "Audit Log",
    description:
      "Last 200 privileged actions across the platform — role changes, deletions, invoice creations. Filterable, immutable, admin-read.",
    tags: ["IMMUTABLE", "200 ROWS", "ADMIN-ONLY"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/admin-audit",
    minRole: "admin",
  },
  {
    label: "ADMIN · DELIVERY",
    name: "Projects",
    description:
      "Client projects and engagements. Auto-created from won leads or added manually. Assign juniors, log hours, generate invoices.",
    tags: ["CRUD", "JUNIOR-LINKED", "ADMIN-ONLY"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/admin-projects",
    minRole: "admin",
  },
  {
    label: "LEADS",
    name: "Leads",
    description:
      "Companies hiring DevOps engineers right now. Paste a contact email, we draft the outreach for you.",
    tags: ["DAILY-REFRESH", "DEVOPS ROLES", "ADMIN-ONLY"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/sales-leads",
    minRole: "staff",
    requiredTag: "sales",
  },
  {
    label: "LEADS · OUTREACH",
    name: "Outreach",
    description:
      "Every email before it goes out — cold, follow-up, reply. Read it, fix the tone, hit send. Nothing leaves without you.",
    tags: ["HUMAN-GATED", "EDITABLE", "ADMIN-ONLY"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/sales",
    minRole: "staff",
    requiredTag: "sales",
  },
  {
    label: "LEADS · OVERVIEW",
    name: "Leads Dashboard",
    description:
      "Funnel counters, lead drawer, campaign view, suppression list, and outreach in one place. Replaces the standalone leads + outreach pages.",
    tags: ["FUNNEL", "5 TABS", "ADMIN-ONLY"],
    status: "LIVE",
    statusColor: "var(--nd-success)",
    href: "/sales-dashboard",
    minRole: "staff",
    requiredTag: "sales",
  },
];

const Tools = () => {
  const { role, tags } = useAuth();
  // role/tags undefined on SSR + during AuthContext load → default safely
  const currentRole = role || "public";
  const currentTags = tags || [];
  const visible = tools.filter((t) => {
    const roleOk = hasRole(currentRole, t.minRole || "public");
    const tagOk = !t.requiredTag
      || currentRole === "admin"
      || hasTag(currentTags, t.requiredTag);
    return roleOk && tagOk;
  });

  return (
    <SectionStyled id="tools">
      <Container>
        <SectionLabel>WHAT WE'RE BUILDING</SectionLabel>
        <SectionTitle>Tools we wish existed when we started</SectionTitle>
        <SectionDesc>
          We kept building the same internal tools for every client.
          So we turned them into products. They're opinionated — built
          from 15 years of opinions about what works.
        </SectionDesc>
        <Row>
          {visible.map((tool, index) => {
            const body = (
              <ToolCard>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <ToolLabel style={{ marginBottom: 0 }}>{tool.label}</ToolLabel>
                  <div>
                    <StatusDot color={tool.statusColor} />
                    <StatusLabel>{tool.status}</StatusLabel>
                  </div>
                </div>
                <ToolName>{tool.name}</ToolName>
                <ToolDesc>{tool.description}</ToolDesc>
                <ToolMeta>
                  {tool.tags.map((tag, i) => (
                    <MetaTag key={i}>{tag}</MetaTag>
                  ))}
                </ToolMeta>
              </ToolCard>
            );
            return (
              <Col md="6" lg="4" key={index}>
                {tool.href ? (
                  <ToolCardLink to={tool.href}>{body}</ToolCardLink>
                ) : (
                  body
                )}
              </Col>
            );
          })}
        </Row>
      </Container>
    </SectionStyled>
  );
};

export default Tools;
